import { describeError, useApi } from '@classgo/app-kit';
import type { GradesView } from '@classgo/shared';
import { Badge, Button, Modal } from '@classgo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { refusalOf } from '../../attendance/api';
import { gradebookApi } from '../../gradebook/api';
import type { GradesCell } from '../../gradebook/types';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/dates';
import type { CellNames, PublishAction } from './types';

/**
 * The state of the cell and, with `grades:publish`, its publication: the subject (what a
 * teacher publishes) or the whole group (the school's publication), each behind a confirmation
 * that says how many students have a grade; and, once published, the reopening with the reason
 * the audit keeps. A publish answers with what became visible; the book and the evaluations are
 * refetched so the rows show "Publicada".
 */
export const PublishControls = ({
  cell,
  names,
  view,
  canPublish,
}: {
  cell: GradesCell;
  names: CellNames;
  view: GradesView;
  canPublish: boolean;
}) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const [action, setAction] = useState<PublishAction | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = async () => {
    await queries.invalidateQueries({ queryKey: ['school', 'period-grades'] });
    await queries.invalidateQueries({ queryKey: ['school', 'evaluations'] });
  };
  const publish = useMutation({
    mutationFn: (scope: 'subject' | 'group') =>
      gradebookApi(api).publish({
        groupId: cell.groupId,
        periodId: cell.periodId,
        ...(scope === 'subject' ? { subjectId: cell.subjectId } : {}),
      }),
    onSuccess: async (result) => {
      setAction(null);
      setNotice(
        t('gradebook.publish.done', {
          published: result.published,
          withoutGrade: result.withoutGrade,
        }),
      );
      await refresh();
    },
  });
  const reopen = useMutation({
    mutationFn: () =>
      gradebookApi(api).reopen({
        groupId: cell.groupId,
        periodId: cell.periodId,
        subjectId: cell.subjectId,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      setAction(null);
      setReason('');
      setNotice(t('gradebook.reopen.done'));
      await refresh();
    },
  });
  const open = (next: PublishAction) => {
    setNotice(null);
    setReasonError(null);
    publish.reset();
    reopen.reset();
    setAction(next);
  };
  const close = () => setAction(null);
  const withGrade = view.rows.filter((row) => row.periodGrade !== null).length;
  const withoutGrade = view.rows.length - withGrade;
  const failed = action === 'reopen' ? reopen : publish;
  const confirm = () => {
    if (action === 'reopen') {
      if (reason.trim().length < 3) {
        setReasonError(t('gradebook.reopen.reasonRequired'));
        return;
      }
      reopen.mutate();
    } else if (action) publish.mutate(action);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
          <Badge tone={view.published ? 'success' : 'gray'}>
            {view.published ? t('grades.view.published') : t('grades.view.draft')}
          </Badge>
          <span>
            {view.published
              ? view.publishedAt
                ? t('gradebook.state.published', { date: formatDate(view.publishedAt) })
                : t('gradebook.state.locked')
              : t('gradebook.state.draft')}
          </span>
        </div>
        {canPublish ? (
          <div className="flex flex-col gap-2 sm:flex-row">
            {view.published ? (
              <Button variant="secondary" onClick={() => open('reopen')}>
                {t('gradebook.reopen')}
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => open('group')}>
                  {t('gradebook.publish.group')}
                </Button>
                <Button onClick={() => open('subject')} disabled={withGrade === 0}>
                  {t('gradebook.publish.subject')}
                </Button>
              </>
            )}
          </div>
        ) : null}
      </div>
      {notice ? (
        <p
          role="status"
          className="rounded-control bg-success-100 px-3 py-2 text-sm text-success-700"
        >
          {notice}
        </p>
      ) : null}
      <Modal
        open={action !== null}
        onClose={close}
        title={
          action === 'group'
            ? t('gradebook.publish.group.title', names)
            : action === 'reopen'
              ? t('gradebook.reopen.title', names)
              : t('gradebook.publish.subject.title', names)
        }
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={action === 'reopen' ? 'danger' : 'primary'}
              loading={publish.isPending || reopen.isPending}
              onClick={confirm}
            >
              {action === 'reopen' ? t('gradebook.reopen') : t('gradebook.publish.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            {action === 'group'
              ? t('gradebook.publish.group.body')
              : action === 'reopen'
                ? t('gradebook.reopen.body')
                : t('gradebook.publish.subject.body', { withGrade, withoutGrade })}
          </p>
          {action === 'reopen' ? (
            <div>
              <label htmlFor="reopen-reason" className="block text-sm font-medium text-gray-900">
                {t('gradebook.reopen.reason')}
              </label>
              <textarea
                id="reopen-reason"
                rows={3}
                maxLength={500}
                aria-invalid={reasonError ? true : undefined}
                className="mt-1 w-full rounded-control border border-gray-300 p-2 text-sm"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                  setReasonError(null);
                }}
              />
              {reasonError ? <p className="mt-1 text-sm text-danger-700">{reasonError}</p> : null}
            </div>
          ) : null}
          {failed.isError ? (
            <p
              role="alert"
              className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700"
            >
              {action === 'reopen' ? t('gradebook.reopen.failed') : t('gradebook.publish.failed')}{' '}
              {refusalOf(failed.error) ?? describeError(t, failed.error)}
            </p>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};
